import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { ILegalClientFullObject } from '../../Interfaces/ILegalClient';
import {
  ILegalClientAccounting,
  initBankInformation,
} from '../../Interfaces/ILegalClientAccounting';
import { BankInformationDialog } from './BankInformationDialog';

interface IProps {
  formValues: ILegalClientFullObject;
  setFormValues: Dispatch<SetStateAction<ILegalClientFullObject>>;
}

export const BankInformationTab = ({ formValues, setFormValues }: IProps) => {
  const [isMount, setIsMount] = useState(false);
  const [selectedBankInformation, setSelectedBankInformation] =
    useState<ILegalClientAccounting>();

  const onAdd = useCallback(
    (newValue: ILegalClientAccounting) =>
      setFormValues(prev => ({
        ...prev,
        AccountingArray: [...prev.AccountingArray, newValue],
      })),
    [setFormValues]
  );

  const onRemove = useCallback(
    (id: number) =>
      setFormValues(prev => ({
        ...prev,
        AccountingArray: prev.AccountingArray.filter(
          ci => ci.idAccounting !== id
        ),
      })),
    [setFormValues]
  );

  const onUpdate = useCallback(
    (newValue: ILegalClientAccounting) => {
      setFormValues(prev => ({
        ...prev,
        AccountingArray: prev.AccountingArray.map(ci =>
          ci.idAccounting === newValue.idAccounting ? newValue : ci
        ),
      }));

      setSelectedBankInformation(undefined);
    },
    [setFormValues, setSelectedBankInformation]
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h5'>Bankinformationen</Typography>
        <Tooltip title='Bankinformation hinzufügen'>
          <IconButton onClick={() => setIsMount(true)}>
            <Add />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={2} mt={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bank</TableCell>
              <TableCell>IBAN</TableCell>
              <TableCell>BIC</TableCell>
              <TableCell sx={{ width: 150 }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formValues.AccountingArray.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <i>Keine Bankinformationen vorhanden.</i>
                </TableCell>
              </TableRow>
            ) : (
              formValues.AccountingArray.map((a, i) => (
                <TableRow key={i}>
                  <TableCell>{a.Bank}</TableCell>
                  <TableCell>{a.IBAN}</TableCell>
                  <TableCell>{a.BIC}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setSelectedBankInformation(a);
                        setIsMount(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => onRemove(a.idAccounting)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Grid>
      {isMount && (
        <BankInformationDialog
          defaultValues={selectedBankInformation ?? initBankInformation()}
          unmount={newValue => {
            if (newValue && newValue.idAccounting > 0) {
              onUpdate(newValue);
            } else if (newValue) {
              onAdd(newValue);
            }
            setSelectedBankInformation(undefined);
            setIsMount(false);
          }}
        />
      )}
    </Box>
  );
};
